<script setup>
import DisclosureSection from "../DisclosureSection.vue";
import { useI18n } from "vue-i18n";
import _isEmpty from "lodash/isEmpty";
import _map from "lodash/map";
import _cloneDeep from "lodash/cloneDeep";
import { useForm } from "@/form";
import { FormInput, FormListbox, FormMultiselect, FormSwitch, FormTags } from "@Components/Form";
import { computed } from "vue";

const props = defineProps(["modelValue", "templates", "statuses", "categories", "postTypes", "users"]);
const emit = defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();

const formattedUsers = computed(() => {
    let users = _cloneDeep(props.users);

    return users.map((u) => {
        u.name = u.data.display_name;
        return u;
    });
});

const fetchTags = async (query) => {
    if (_isEmpty(query)) return [];

    let data = await useForm({ query: query }).post("wordpress_tags");
    return data.map((tag) => {
        return {
            value: tag.name,
            label: tag.name,
        };
    });
};
</script>

<template>
    <DisclosureSection v-if="modelValue.type !== 'regenerate'" :title="__('Paramètres WordPress')" default-open>
        <div class="p-3 space-y-6">
            <FormListbox
                :readonly="modelValue.type === 'siloing'"
                v-model="modelValue.modules.wordpress.post_type"
                :items="postTypes"
                item-key="name"
                item-display="label"
                required
                :errors="modelValue.getErrors('modules.wordpress.post_type')"
                :z="1">
                <template #label>
                    <span class="text-white">{{ __("Type de post") }}</span>
                </template>
            </FormListbox>

            <FormInput
                v-if="modelValue.modules.wordpress.post_type === 'page'"
                v-model="modelValue.modules.wordpress.parent_page_id"
                :errors="modelValue.getErrors('modules.wordpress.parent_page_id')"
                :z="1">
                <template #label>
                    <span class="text-white">{{ __("ID de la page parent") }}</span>
                </template>
            </FormInput>

            <FormListbox
                v-model="modelValue.modules.wordpress.template"
                :items="_map(templates, (key, value) => ({ name: value, key: key }))"
                item-key="key"
                item-display="name"
                :errors="modelValue.getErrors('modules.wordpress.template')"
                :z="1">
                <template #label>
                    <span class="text-white">{{ __("Template de page") }}</span>
                </template>
            </FormListbox>

            <FormListbox
                v-model="modelValue.modules.wordpress.status"
                :items="_map(statuses, (value, key) => ({ name: value, key: key }))"
                item-key="key"
                item-display="name"
                required
                :errors="modelValue.getErrors('modules.wordpress.status')"
                :z="1">
                <template #label>
                    <span class="text-white">{{ __("Statut") }}</span>
                </template>
            </FormListbox>

            <FormListbox
                :readonly="modelValue.persona_id"
                :help="modelValue.persona_id ? __('Configuré avec le Persona.') : undefined"
                v-model="modelValue.modules.wordpress.author_id"
                :items="formattedUsers"
                item-key="ID"
                item-display="name"
                :z="1">
                <template #label>
                    <span class="text-white">{{ __("Auteur") }}</span>
                </template>
            </FormListbox>

            <template v-if="modelValue.modules.wordpress.post_type !== 'page'">
                <FormSwitch v-model="modelValue.modules.wordpress.categories.automatic.enabled" :z="1">
                    <span class="text-white">{{ __("Associer automatiquement avec l'IA vos publications dans les catégories") }}</span>
                </FormSwitch>

                <div v-if="!modelValue.modules.wordpress.categories.automatic.enabled">
                    <div class="block text-sm font-medium leading-6 text-gray-200">{{ __("Catégories") }}</div>
                    <div class="max-h-64 overflow-y-auto px-3 mt-2">
                        <div v-for="category in categories" :key="category.term_id" class="space-y-5">
                            <div class="relative flex items-start">
                                <div class="h-6 flex items-center">
                                    <input
                                        v-model="modelValue.modules.wordpress.categories.custom"
                                        :id="'category-' + category.term_id"
                                        name="categories"
                                        type="checkbox"
                                        :value="category.term_id"
                                        class="w-4 h-4 text-gray-700 bg-gray-700 rounded border-gray-600 focus:ring-gray-600 focus:ring-offset-gray-900" />
                                </div>
                                <div class="ml-3 text-sm leading-6">
                                    <label :for="'category-' + category.term_id" class="font-medium text-gray-200">{{ category.name }}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <template v-if="modelValue.modules.wordpress.post_type !== 'page'">
                    <FormSwitch v-model="modelValue.modules.wordpress.tags.automatic.enabled" :z="1">
                        <span class="text-white">{{ __("Générer des étiquettes automatiques avec l'IA") }}</span>
                    </FormSwitch>
                </template>

                <FormTags v-model="modelValue.modules.wordpress.tags.custom" :additionalSplitters="[',']" :z="1">
                    <template #label>
                        <span class="text-white">{{ __("Ajouter des étiquettes personnalisées") }}</span>
                    </template>
                </FormTags>
            </template>

            <FormSwitch v-model="modelValue.modules.wordpress.rewrite_permalink.enabled" :z="1">
                <span class="text-white">{{ __("Réécrire le permalien") }}</span>
            </FormSwitch>
        </div>
    </DisclosureSection>
</template>
